import React, { memo, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

const closeIcon = icon({ name: "xmark", family: "sharp", style: "regular" });

/**
 * ValueChip - Individual chip for a selected value
 */
const ValueChip = memo(({ value, onRemove }) => {
	const handleRemove = useCallback((e) => {
		e.stopPropagation();
		onRemove?.(value);
	}, [onRemove, value]);

	const handleKeyDown = useCallback((e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			onRemove?.(value);
		}
	}, [onRemove, value]);

	return (
		<div className="inline-flex items-center bg-surface-2 hover:bg-surface-3 rounded-full px-2 py-1 text-xs group">
			<span className="text-text truncate max-w-[120px]" title={value}>
				{value}
			</span>
			<button
				type="button"
				onClick={handleRemove}
				onKeyDown={handleKeyDown}
				className="ml-1 w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-border cursor-pointer"
				aria-label={`Remove ${value}`}
			>
				<FontAwesomeIcon
					icon={closeIcon}
					className="text-text-2 text-[10px]"
				/>
			</button>
		</div>
	);
});

ValueChip.displayName = 'ValueChip';

ValueChip.propTypes = {
	value: PropTypes.string.isRequired,
	onRemove: PropTypes.func,
};

/**
 * MoreChip - Chip showing "+x more" that expands when clicked
 */
const MoreChip = memo(({ count, onClick }) => {
	const handleClick = useCallback((e) => {
		e.stopPropagation();
		onClick?.();
	}, [onClick]);

	const handleKeyDown = useCallback((e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onClick?.();
		}
	}, [onClick]);

	return (
		<button
			type="button"
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			className="inline-flex items-center bg-surface-2 hover:bg-surface-3 rounded-full px-2 py-1 text-xs text-text cursor-pointer transition-colors duration-150"
			aria-label={`Show ${count} more selected values`}
		>
			+{count} more
		</button>
	);
});

MoreChip.displayName = 'MoreChip';

MoreChip.propTypes = {
	count: PropTypes.number.isRequired,
	onClick: PropTypes.func,
};

/**
 * SelectedValuesChips - Displays selected values as inline chips
 * 
 * Shows max 5 values by default, with "+x more" chip that expands inline.
 * Wraps over two lines when values don't fit.
 */
const SelectedValuesChips = memo(({ values, onRemove, maxVisible = 5 }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const handleExpand = useCallback(() => {
		setIsExpanded(true);
	}, []);

	const visibleValues = useMemo(() => {
		if (isExpanded || values.length <= maxVisible) {
			return values;
		}
		return values.slice(0, maxVisible);
	}, [values, maxVisible, isExpanded]);

	const hiddenCount = values.length - maxVisible;
	const showMoreChip = !isExpanded && hiddenCount > 0;

	if (values.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-wrap gap-1.5 max-h-[60px] overflow-y-auto cursor-pointer">
			{visibleValues.map((value, index) => (
				<ValueChip
					key={`${index}-${value}`}
					value={value}
					onRemove={onRemove}
				/>
			))}
			{showMoreChip && (
				<MoreChip
					count={hiddenCount}
					onClick={handleExpand}
				/>
			)}
		</div>
	);
});

SelectedValuesChips.displayName = 'SelectedValuesChips';

SelectedValuesChips.propTypes = {
	/** Array of selected value strings */
	values: PropTypes.arrayOf(PropTypes.string).isRequired,
	/** Callback when a value is removed - receives the value string */
	onRemove: PropTypes.func,
	/** Maximum number of visible chips before showing "+x more" */
	maxVisible: PropTypes.number,
};

SelectedValuesChips.defaultProps = {
	maxVisible: 5,
};

export default SelectedValuesChips;
